<script setup lang="ts">
import { computed, markRaw, ref } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { TrashIcon, PlusIcon, ArrowPathIcon } from '@heroicons/vue/24/solid';
import { useInspectorStore } from '@/stores/inspectorStore';
import AddOwnerInspectorContent from '@/components/arrayColumns/AddOwnerInspectorContent.vue';
import http from '@/services/http';

const props = defineProps<{
  field: string;
  dataType: string;
  data: Record<string, unknown>;
  classType: string;
}>();

const filterText = ref('');
const isRemoving = ref<number | null>(null);
const removedIds = ref<Set<number>>(new Set());
const queryClient = useQueryClient();
const inspectorStore = useInspectorStore();

// Get the children array data
// Field pattern: 'owners.display_string' where 'owners' is the array field
const childrenArray = computed(() => {
  const parts = props.field.split('.');
  if (parts.length !== 2 || parts[1] !== 'display_string') {
    return [];
  }
  const arrayField = parts[0];
  const arrayData = props.data[arrayField];
  if (!Array.isArray(arrayData)) return [];
  return arrayData.filter((item: { id?: number }) =>
    item?.id == null || !removedIds.value.has(item.id)
  );
});

const contactId = computed(() => props.data.id as number);

// Filter items based on filter text (searches owner type and display_string)
const filteredItems = computed(() => {
  if (!filterText.value.trim()) {
    return childrenArray.value;
  }
  const searchText = filterText.value.toLowerCase();
  return childrenArray.value.filter((item: { owner_type?: string; display_string?: string }) => {
    const type = (item?.owner_type || '').toLowerCase();
    const display = (item?.display_string || '').toLowerCase();
    return type.includes(searchText) || display.includes(searchText);
  });
});

const excludeOwners = computed(() =>
  childrenArray.value.map((item: { owner_type: string; owner_id: number }) => ({
    owner_type: item.owner_type,
    owner_id: item.owner_id
  }))
);

function openAddOwner() {
  inspectorStore.push('Add Owner', markRaw(AddOwnerInspectorContent), {
    contactId: contactId.value,
    excludeOwners: excludeOwners.value,
  });
}

async function removeOwner(contactableId: number) {
  if (isRemoving.value || !contactId.value) return;
  isRemoving.value = contactableId;
  try {
    await http.delete(`/api/contactable/${contactableId}`);
    const next = new Set(removedIds.value);
    next.add(contactableId);
    removedIds.value = next;
    await queryClient.invalidateQueries({ queryKey: ['search'] });
  } finally {
    isRemoving.value = null;
  }
}

function ownerLabel(item: { owner_type?: string; display_string?: string }) {
  if (item?.owner_type && item?.display_string) {
    return `${item.owner_type}: ${item.display_string}`;
  }
  return item?.display_string || item?.owner_type || '—';
}
</script>

<template>
  <div class="flex flex-col flex-1 min-h-0 overflow-hidden">
    <div class="py-3 border-b flex items-center justify-between gap-2 px-4">
      <Input
        v-model="filterText"
        placeholder="Filter owners..."
        class="flex-1"
      />
      <button
        type="button"
        class="inline-flex items-center justify-center w-7 h-7 rounded-md border border-muted-foreground/50 text-muted-foreground hover:border-muted-foreground hover:text-foreground transition-colors shrink-0"
        aria-label="Add owner"
        @click="openAddOwner"
      >
        <PlusIcon class="h-4 w-4" />
      </button>
    </div>
    <div class="flex-1 min-h-0 overflow-y-auto px-4">
      <Table>
        <TableBody>
          <template v-if="filteredItems.length > 0">
            <TableRow
              v-for="(item, index) in filteredItems"
              :key="item?.id ?? index"
            >
              <TableCell>
                {{ ownerLabel(item) }}
              </TableCell>
              <TableCell class="w-12">
                <ArrowPathIcon
                  v-if="isRemoving === item?.id"
                  class="h-4 w-4 animate-spin text-muted-foreground"
                />
                <button
                  v-else
                  type="button"
                  :disabled="isRemoving !== null"
                  class="inline-flex items-center justify-center w-6 h-6 rounded-sm hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
                  aria-label="Remove owner"
                  @click="item?.id != null ? removeOwner(item.id) : undefined"
                >
                  <TrashIcon class="h-4 w-4" />
                </button>
              </TableCell>
            </TableRow>
          </template>
          <TableRow v-else>
            <TableCell colspan="2" class="text-center text-muted-foreground py-6">
              No owners. Use + to add Account, Location, or LocationZone.
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  </div>
</template>
